<template>
  <database-layout>
    <base-heading size="lg">{{ child ? step.concat(': ').concat(child) : step }}</base-heading>
    <!--
      Two tables, one with source, one with dest. Compare by ID
      Checkboxes on rows and columns so you can select individual rows/columns to sync.
      Check all buttons in the top left corner with a line to differentiate between select all rows and select all cols
    -->
    <div class="pt-1 pb-10 font-bold text-green-700" v-if="!(Object.keys(diffData)).length && !newRows.length">Nothing to sync here!</div>

    <div class="w-full overflow-hidden space-y-5">
      <div class="flex justify-between">
        <div class="space-x-2">
          <base-button small @click="toggleAllRows">{{ checked.rows.length ? 'Uncheck All' : 'Check All' }} Rows</base-button>
          <base-button small @click="toggleShowDiffs">{{  showOnlyDiffs ? 'Show All Rows' : 'Show Only Rows With Changes' }}</base-button>
          <base-button small @click="toggleShowSelected">{{  showOnlySelected ? 'Show All Rows' : 'Show Only Checked Rows' }}</base-button>

          <!-- <base-button small @click="toggleAllCols">{{ checked.cols.length ? 'Uncheck All' : 'Check All' }}  Columns</base-button> -->
        </div>
        <div class="flex gap-2 items-center">
          <base-label no-margin>Key: </base-label>
          <base-tooltip>
            <template v-slot:content>
              <div>
                When there is an ID collision<br>
                (a new item is created in both databases),<br>
                it may be necessary to import the<br>
                record with a new ID. Click this<br>
                icon to disconnect the two records<br>
                and import the item with a new ID
              </div>
            </template>
            <svg class="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M304.1 405.9c4.7 4.7 4.7 12.3 0 17l-44.7 44.7c-59.3 59.3-155.7 59.3-215 0-59.3-59.3-59.3-155.7 0-215l44.7-44.7c4.7-4.7 12.3-4.7 17 0l39.6 39.6c4.7 4.7 4.7 12.3 0 17l-44.7 44.7c-28.1 28.1-28.1 73.8 0 101.8 28.1 28.1 73.8 28.1 101.8 0l44.7-44.7c4.7-4.7 12.3-4.7 17 0l39.6 39.6zm-56.6-260.2c4.7 4.7 12.3 4.7 17 0l44.7-44.7c28.1-28.1 73.8-28.1 101.8 0 28.1 28.1 28.1 73.8 0 101.8l-44.7 44.7c-4.7 4.7-4.7 12.3 0 17l39.6 39.6c4.7 4.7 12.3 4.7 17 0l44.7-44.7c59.3-59.3 59.3-155.7 0-215-59.3-59.3-155.7-59.3-215 0l-44.7 44.7c-4.7 4.7-4.7 12.3 0 17l39.6 39.6zm234.8 359.3l22.6-22.6c9.4-9.4 9.4-24.6 0-33.9L63.6 7c-9.4-9.4-24.6-9.4-33.9 0L7 29.7c-9.4 9.4-9.4 24.6 0 33.9l441.4 441.4c9.4 9.4 24.6 9.4 33.9 0z"/></svg>
          </base-tooltip>
          <base-tooltip>
            <template v-slot:content>
              <div>
                Indicates that the data item that is differing<br>
                between the two databases will be imported.
              </div>
            </template>
            <check-circle-icon class="w-5 h-5" />
          </base-tooltip>
          <base-tooltip>
            <template v-slot:content>
              <div>
                Indicates that the data item that is differing<br>
                between the two databases will NOT be imported.
              </div>
            </template>
            <x-circle-icon class="w-5 h-5" />
          </base-tooltip>
          <base-tooltip>
            <template v-slot:content>
              <span class="block w-56">This indicates that the row or column is not present in the database being imported. This will not delete what exists on the current database.</span>
            </template>
            <span class="block w-4 h-4 bg-red-500 border border-red-700"></span>
          </base-tooltip>
          <base-tooltip>
            <template v-slot:content>
              <span class="block w-56">This indicates that the row or column is not present in the current database. This will be imported.</span>
            </template>
            <span class="block w-4 h-4 bg-green-500 border border-green-700"></span>
          </base-tooltip>

          <base-tooltip>
            <template v-slot:content>
              <span class="block w-56">This indicates that the data differs between the current database and the importing database. This data will only be updated if the row is checked or the individual data is selected to be imported.</span>
            </template>
            <span class="block w-4 h-4 bg-yellow-500 border border-yellow-700"></span>
          </base-tooltip>
        </div>
      </div>
      <div class="overflow-x-auto overflow-y-visible">
        <table class="divide-y divide-gray-300">
          <thead>
            <tr class="divide-x divide-gray-200">
              <!-- TODO: Add select all rows and select all cols in first heading col -->
              <th scope="col" class="py-3.5 px-2 text-left text-sm font-semibold text-gray-900 opacity-0">Un/Link</th>
              <th v-if="allowSelect" scope="col" class="py-3.5 px-2 text-left text-sm font-semibold text-gray-900 opacity-0">Select</th>
              <th v-for="(col, idxC) in cols" :key="`${uuid}-col-${idxC}`" scope="col" class="px-4 py-3.5 text-left text-sm font-semibold text-gray-900">
                <p class="flex flex-col gap-2 flex-nowrap items-start">
                  <!-- <span><input type="checkbox" :name="`${uuid}-checked-cols`" v-model="checked.cols" :value="col" /></span> -->
                  <span>{{ col }}</span>
                </p>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white">
            <tr v-for="(row, idxR) in rows" :key="`${uuid}-row-${idxR}`" v-show="(!showOnlySelected || (showOnlySelected && checked.rows.includes(row.id))) && (!showOnlyDiffs || (showOnlyDiffs && (typeof diffData[row.id] !== 'undefined' || delRowIds.includes(row.id) || newRowIds.includes(row.id))))" :class="`divide-x divide-gray-200 ${delRowIds.includes(row.id) ? 'bg-red-500 text-red-50' : (newRowIds.includes(row.id) ? 'bg-green-500 text-green-50' : 'text-gray-500')}`">
              <td class="whitespace-nowrap py-4 px-2 text-center relative">

                <div v-if="(typeof diffData[row.id] !== 'undefined')" class="absolute left-1 top-1/2 transform -translate-y-1/2 w-1 h-1 rounded-full bg-yellow-500"></div>

                <base-tooltip v-if="importAsNew.includes(row.id)">
                  <template v-slot:content>
                    <div>
                      Re-Connect records
                    </div>
                  </template>
                  <base-button icon small @click="relinkRow(row.id)">
                    <svg class="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M326.6 185.4c59.7 59.8 58.9 155.7 .4 214.6-.1 .1-.2 .3-.4 .4l-67.2 67.2c-59.3 59.3-155.7 59.3-215 0-59.3-59.3-59.3-155.7 0-215l37.1-37.1c9.8-9.8 26.8-3.3 27.3 10.6 .6 17.7 3.8 35.5 9.7 52.7 2 5.8 .6 12.3-3.8 16.6l-13.1 13.1c-28 28-28.9 73.7-1.2 102 28 28.6 74.1 28.7 102.3 .5l67.2-67.2c28.2-28.2 28.1-73.8 0-101.8-3.7-3.7-7.4-6.6-10.3-8.6a16 16 0 0 1 -6.9-12.6c-.4-10.6 3.3-21.5 11.7-29.8l21.1-21.1c5.5-5.5 14.2-6.2 20.6-1.7a152.5 152.5 0 0 1 20.5 17.2zM467.5 44.4c-59.3-59.3-155.7-59.3-215 0l-67.2 67.2c-.1 .1-.3 .3-.4 .4-58.6 58.9-59.4 154.8 .4 214.6a152.5 152.5 0 0 0 20.5 17.2c6.4 4.5 15.1 3.8 20.6-1.7l21.1-21.1c8.4-8.4 12.1-19.2 11.7-29.8a16 16 0 0 0 -6.9-12.6c-2.9-2-6.6-4.9-10.3-8.6-28.1-28.1-28.2-73.6 0-101.8l67.2-67.2c28.2-28.2 74.3-28.1 102.3 .5 27.8 28.3 26.9 73.9-1.2 102l-13.1 13.1c-4.4 4.4-5.8 10.8-3.8 16.6 5.9 17.2 9 35 9.7 52.7 .5 13.9 17.5 20.4 27.3 10.6l37.1-37.1c59.3-59.3 59.3-155.7 0-215z"/></svg>
                  </base-button>
                </base-tooltip>
                <base-tooltip v-else-if="(typeof diffData[row.id] !== 'undefined')">
                  <template v-slot:content>
                    <div>
                      Disconnect records
                    </div>
                  </template>
                  <base-button icon small @click="unlinkRow(row.id)">
                    <svg class="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M304.1 405.9c4.7 4.7 4.7 12.3 0 17l-44.7 44.7c-59.3 59.3-155.7 59.3-215 0-59.3-59.3-59.3-155.7 0-215l44.7-44.7c4.7-4.7 12.3-4.7 17 0l39.6 39.6c4.7 4.7 4.7 12.3 0 17l-44.7 44.7c-28.1 28.1-28.1 73.8 0 101.8 28.1 28.1 73.8 28.1 101.8 0l44.7-44.7c4.7-4.7 12.3-4.7 17 0l39.6 39.6zm-56.6-260.2c4.7 4.7 12.3 4.7 17 0l44.7-44.7c28.1-28.1 73.8-28.1 101.8 0 28.1 28.1 28.1 73.8 0 101.8l-44.7 44.7c-4.7 4.7-4.7 12.3 0 17l39.6 39.6c4.7 4.7 12.3 4.7 17 0l44.7-44.7c59.3-59.3 59.3-155.7 0-215-59.3-59.3-155.7-59.3-215 0l-44.7 44.7c-4.7 4.7-4.7 12.3 0 17l39.6 39.6zm234.8 359.3l22.6-22.6c9.4-9.4 9.4-24.6 0-33.9L63.6 7c-9.4-9.4-24.6-9.4-33.9 0L7 29.7c-9.4 9.4-9.4 24.6 0 33.9l441.4 441.4c9.4 9.4 24.6 9.4 33.9 0z"/></svg>
                  </base-button>
                </base-tooltip>
                <base-tooltip v-else>
                  <template v-slot:content>
                    <div>
                      Record is exactly the same or is new.
                    </div>
                  </template>
                  <base-button disabled icon small>
                    <svg class="w-4 h-4 fill-current opacity-50" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M304.1 405.9c4.7 4.7 4.7 12.3 0 17l-44.7 44.7c-59.3 59.3-155.7 59.3-215 0-59.3-59.3-59.3-155.7 0-215l44.7-44.7c4.7-4.7 12.3-4.7 17 0l39.6 39.6c4.7 4.7 4.7 12.3 0 17l-44.7 44.7c-28.1 28.1-28.1 73.8 0 101.8 28.1 28.1 73.8 28.1 101.8 0l44.7-44.7c4.7-4.7 12.3-4.7 17 0l39.6 39.6zm-56.6-260.2c4.7 4.7 12.3 4.7 17 0l44.7-44.7c28.1-28.1 73.8-28.1 101.8 0 28.1 28.1 28.1 73.8 0 101.8l-44.7 44.7c-4.7 4.7-4.7 12.3 0 17l39.6 39.6c4.7 4.7 12.3 4.7 17 0l44.7-44.7c59.3-59.3 59.3-155.7 0-215-59.3-59.3-155.7-59.3-215 0l-44.7 44.7c-4.7 4.7-4.7 12.3 0 17l39.6 39.6zm234.8 359.3l22.6-22.6c9.4-9.4 9.4-24.6 0-33.9L63.6 7c-9.4-9.4-24.6-9.4-33.9 0L7 29.7c-9.4 9.4-9.4 24.6 0 33.9l441.4 441.4c9.4 9.4 24.6 9.4 33.9 0z"/></svg>
                  </base-button>
                </base-tooltip>
              </td>
              <td v-if="allowSelect" class="whitespace-nowrap py-4 px-2 text-center"><input type="checkbox" :name="`${uuid}-checked-rows`" v-model="checked.rows" :value="row.id" :disabled="delRowIds.includes(row.id)" class="disabled:opacity-30 disabled:cursor-not-allowed" @input="rowToggled(row.id)" /></td>
              <!-- <td class="whitespace-nowrap p-4 text-sm font-medium text-gray-900">row.id</td> -->
              <td v-for="(col, idxC) in cols" :key="`${uuid}-row-${idxR}-col-${idxC}`" :class="`whitespace-nowrap p-4 pr-10 text-sm max-w-96 truncate relative ${diffData[row.id] && diffData[row.id].includes(col) && !importAsNew.includes(row.id) ? 'bg-yellow-500 text-yellow-50 cursor-pointer' : ''}`" @click="showDiff(row, col)">
                {{ row[col] }}
                <span class="absolute top-1/2 transform -translate-y-1/2 right-3" v-if="diffData[row.id] && diffData[row.id].includes(col) && !importAsNew.includes(row.id)">
                  <check-circle-icon v-if="dataToImport[row.id] && dataToImport[row.id].includes(col)" class="w-4 h-4" />
                  <x-circle-icon v-else class="w-4 h-4" />
                </span>
              </td>
            </tr>
            <!-- Import As New records / Resolved collisions -->
            <tr v-for="(row, idxR) in importAsNewRows" :key="`${uuid}-new-row-${idxR}`" v-show="(!showOnlySelected || (showOnlySelected && checked.rows.includes(row.id))) && (!showOnlyDiffs || (showOnlyDiffs && (typeof diffData[row.id] !== 'undefined' || delRowIds.includes(row.id) || newRowIds.includes(row.id))))" :class="`divide-x divide-gray-200 bg-green-500 text-green-50`">
              <td class="whitespace-nowrap py-4 px-2 text-center">
                <base-tooltip>
                  <template v-slot:content>
                    <div>
                      Re-Connect to old record
                    </div>
                  </template>
                  <base-button icon small @click="relinkRow(row)">
                    <svg class="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M326.6 185.4c59.7 59.8 58.9 155.7 .4 214.6-.1 .1-.2 .3-.4 .4l-67.2 67.2c-59.3 59.3-155.7 59.3-215 0-59.3-59.3-59.3-155.7 0-215l37.1-37.1c9.8-9.8 26.8-3.3 27.3 10.6 .6 17.7 3.8 35.5 9.7 52.7 2 5.8 .6 12.3-3.8 16.6l-13.1 13.1c-28 28-28.9 73.7-1.2 102 28 28.6 74.1 28.7 102.3 .5l67.2-67.2c28.2-28.2 28.1-73.8 0-101.8-3.7-3.7-7.4-6.6-10.3-8.6a16 16 0 0 1 -6.9-12.6c-.4-10.6 3.3-21.5 11.7-29.8l21.1-21.1c5.5-5.5 14.2-6.2 20.6-1.7a152.5 152.5 0 0 1 20.5 17.2zM467.5 44.4c-59.3-59.3-155.7-59.3-215 0l-67.2 67.2c-.1 .1-.3 .3-.4 .4-58.6 58.9-59.4 154.8 .4 214.6a152.5 152.5 0 0 0 20.5 17.2c6.4 4.5 15.1 3.8 20.6-1.7l21.1-21.1c8.4-8.4 12.1-19.2 11.7-29.8a16 16 0 0 0 -6.9-12.6c-2.9-2-6.6-4.9-10.3-8.6-28.1-28.1-28.2-73.6 0-101.8l67.2-67.2c28.2-28.2 74.3-28.1 102.3 .5 27.8 28.3 26.9 73.9-1.2 102l-13.1 13.1c-4.4 4.4-5.8 10.8-3.8 16.6 5.9 17.2 9 35 9.7 52.7 .5 13.9 17.5 20.4 27.3 10.6l37.1-37.1c59.3-59.3 59.3-155.7 0-215z"/></svg>
                  </base-button>
                </base-tooltip>
              </td>
              <td v-if="allowSelect" class="whitespace-nowrap py-4 px-2 text-center"><input type="checkbox" :name="`${uuid}-checked-rows`" checked :value="`${row.id}-new`" :disabled="true" class="disabled:opacity-30 disabled:cursor-not-allowed" /></td>
              <td v-for="(col, idxC) in cols" :key="`${uuid}-new-row-${idxR}-col-${idxC}`" :class="`whitespace-nowrap p-4 pr-10 text-sm max-w-96 truncate relative`">
                {{ row[col] }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="mt-10">
      <base-button @click="doSync" :disabled="!(Object.keys(diffData)).length && !newRows.length">Sync</base-button>
    </div>
    <div class="flex items-start justify-between gap-2 mt-10">
      <base-button v-if="stepIdx > 0" tag="inertia-link" :href="route('cms.database.sync.step', {step: steps[stepIdx-1].value})">Prev</base-button>
      <base-button v-else tag="inertia-link" :href="route('cms.database.sync.index')">Prev</base-button>

      <base-button v-if="stepIdx < steps.length-1" tag="inertia-link" :href="route('cms.database.sync.step', {step: steps[stepIdx+1].value})">Next</base-button>
      <base-button v-else :disabled="true">Next</base-button>
    </div>
  </database-layout>
</template>
<script>
import DatabaseLayout from '../Layout.vue'
import DatabaseSyncTable from './Table.vue';
import Form from '../../../components/Form';
import { CheckCircleIcon, XCircleIcon, LinkIcon } from '@vue-hero-icons/solid';
import moment from 'moment';
export default {
  components: {
    DatabaseLayout,
    DatabaseSyncTable,
    CheckCircleIcon,
    XCircleIcon,
    LinkIcon,
    ...Form,
  },
  props: {
    step: '',
    child: '',
    steps: {
      type: Array,
      default: () => [],
    },
    rows: {
      type: Array,
      default: () => [],
    },
    cols: {
      type: Array,
      default: () => [],
    },
    newCols: {
      type: Array,
      default: () => [],
    },
    delCols: {
      type: Array,
      default: () => [],
    },
    newRows: {
      type: Array,
      default: () => [],
    },
    delRows: {
      type: Array,
      default: () => [],
    },
    diffData: {
      type: Object,
      default: () => {
        return {}
      }
    },
    importData: {
      type: Array,
      default: () => []
    },
    showDifferences: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      checked: {
        rows: [],
        cols: [],
      },
      dataToImport: {},
      importAsNew: [],
      allowSelect: true,
      showOnlySelected: false,
      showOnlyDiffs: false,
    }
  },
  computed: {
    stepIdx() {
      return this.steps.findIndex((st) => st.label == this.step);
    },
    stepValue() {
      return this.steps.find((st) => st.label == this.step)?.value || null;
    },

    delRowIds() {
      return this.delRows.map(r => r.id);
    },
    newRowIds() {
      return this.newRows.map(r => r.id);
    },
    importAsNewRows() {
      return this.importData.filter(r => this.importAsNew.includes(r.id));
    }
  },
  methods: {
    toggleAllRows() {
      //
      if (this.checked.rows.length) {
        this.checked.rows = [];
      } else {
        if (this.showOnlyDiffs) {
          this.checked.rows = this.rows.map(r => r.id).filter(r => {
            return (typeof this.diffData[r] !== 'undefined' || this.delRowIds.includes(r) || this.newRowIds.includes(r))
          });
        } else {
          this.checked.rows = this.rows.map(r => r.id);
        }
      }
    },
    toggleShowDiffs() {
      if (this.showOnlyDiffs) {
        this.showOnlyDiffs = false;
      } else {
        this.showOnlyDiffs = true;
        this.showOnlySelected = false;
      }
    },
    toggleShowSelected() {
      if (this.showOnlySelected) {
        this.showOnlySelected = false;
      } else {
        this.showOnlyDiffs = false;
        this.showOnlySelected = true;
      }
    },
    // toggleAllCols() {
    //   //
    //   if (this.checked.cols.length) {
    //     this.checked.cols = [];
    //   } else {
    //     // TODO change to col name
    //     this.checked.cols = Array.from({ length: this.cols.length }, (value, index) => index)
    //   }
    // },
    toggleDataToImport(row, col) {
      if (typeof this.dataToImport[row.id] === 'undefined') {
        this.dataToImport[row.id] = [col];
        this.checked.rows.push(row.id);
      } else if (Array.isArray(this.dataToImport[row.id])) {
        if (this.dataToImport[row.id].includes(col)) {
          this.dataToImport[row.id].splice(this.dataToImport[row.id].indexOf(col), 1);

          if (!this.dataToImport[row.id].length) {
            if (this.checked.rows.includes(row.id)) {
              this.checked.rows.splice(this.checked.rows.indexOf(row.id), 1)
            }
            delete this.dataToImport[row.id];
          }
        } else {
          this.dataToImport[row.id].push(col);
        }
      } else {
        console.error('showDiff popup confirmation had error');
      }
    },
    rowToggled(id) {
      // Add/Remove to/from dataToImport
      if (this.dataToImport[id]) {
        delete this.dataToImport[id];
      } else {
        if (this.diffData[id] && Array.isArray(this.diffData[id])) {
          this.dataToImport[id] = this.diffData[id].map(d => d)
        }
      }
    },
    unlinkRow(id) {
      this.importAsNew.push(id);
    },
    relinkRow(id) {
      this.importAsNew.splice(this.importAsNew.indexOf(id), 1);
    },
    tabularizeJsonArray(data) {
      if (Array.isArray(data)) {
        if (data.length) {
          if (typeof data[0] === 'object') {
            const cols = Object.keys(data[0]);
            let table = '<div class="overflow-x-auto overflow-y-visible"><table class="divide-y divide-gray-300"><thead><tr class="divide-x divide-gray-200">';
            cols.forEach(function(col) {
              table += `<td class="w-10 whitespace-nowrap min-w-fit p-2">${col}</td>`;
            });
            table += '</tr></thead><tbody class="divide-y divide-gray-200">';
            data.forEach(function(row) {
              table += '<tr class="divide-x divide-gray-200">';
              cols.forEach(function(col) {
                table += `<td class="p-2 w-10 whitespace-nowrap min-w-fit max-w-96 overflow-hidden">${typeof row[col] === 'object' ? JSON.stringify(row[col]) : row[col]}</td>`;
              });
              table += '</tr>';
            });
            table += '</tbody></table></div>';
            return table;
          } else {
            return 'Error (TJA2)'
          }
        } else {
          return '';
        }
      } else {
        return 'Error (TJA1)';
      }
    },
    showDiff(row, col) {
      if (this.diffData[row.id] && this.diffData[row.id].includes(col)) {
        // TODO: pass to tablularizeJsonArray diffs so it can color code. low priority.
        const importRow = this.importData.find(d => d.id == row.id)
        if (importRow) {
          this.$swal.fire({
            width: '80%',
            title: 'Data Differences',
            html: `
            <div class="flex">
              <div class="w-1/2 pr-4 border-r border-black">
                <p class="mb-2"><strong>Current Database:</strong></p>
                <div>${(Array.isArray(row[col])) ? this.tabularizeJsonArray(row[col]) : (typeof row[col] === 'string' ? row[col] : JSON.stringify(row[col]))}</div>
                <p class="mt-4">Updated At: ${row.updated_at}</p>
              </div>
              <div class="w-1/2 pl-4">
                <p class="mb-2"><strong>Importing Database:</strong></p>
                <div>${(Array.isArray(importRow[col])) ? this.tabularizeJsonArray(importRow[col]) : (typeof importRow[col] === 'string' ? importRow[col] : JSON.stringify(importRow[col]))}</div>
                <p class="mt-4">Updated At: ${importRow.updated_at}</p>
              </div>
            </div>`,
            showCancelButton: true,
            cancelButtonText: 'Close',
            confirmButtonText: this.dataToImport[row.id] && Array.isArray(this.dataToImport[row.id]) && this.dataToImport[row.id].includes(col) ? 'Remove from Import' : 'Add to Import',
          }).then((result) => {
            if (result.isConfirmed) {
              this.toggleDataToImport(row, col)
            }
          });
        }
      }
    },
    doSync() {
      this.$swal.fire({
        title: 'Sync',
        text: 'All checked rows will be synced from the import database to the current database. This cannot be undone. Are you sure?',
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Sync',
      }).then((result) => {
        if (result.isConfirmed) {
          this.$CMS_POST('database.sync.step', this.route().params, {
            importIds: this.checked.rows,
            dataToImport: this.dataToImport,
            importIdsAsNew: this.importAsNewRows,
          }).then((resp) => {
            console.log(resp);
            this.$inertia.reload();
          });
          // TODO: Send: step, child, checked.rows, dataToImport, importAsNewRows
          // TODO: Pray
        }
      });
    }
  },
  mounted() {
    this.rows.forEach(row => {
      if (typeof this.diffData[row.id] !== 'undefined' && Array.isArray(this.diffData[row.id])) {
        // Get row from importData
        const importRow = this.importData.find(d => d.id == row.id);
        if (importRow) {
          // Check if row updated_at is < than importRow version
          if (moment(row.updated_at).isBefore(importRow.updated_at)) {
            // Loop through cols
            this.diffData[row.id].forEach(col => {
              // Mark each col as an import
              this.toggleDataToImport(row, col)
            });
          }
        }
      } else if (this.newRows.find(nr => nr.id == row.id)) {
        this.checked.rows.push(row.id)
      }
    });
  }

}
</script>
